import React from 'react';

import {
  reportLatencyFinished as reportLatencyFinishedToDesigner,
  CANVAS_APP_STARTUP_LATENCY_MAP,
  CANVAS_APP_SWITCH_LATENCY_MAP,
  detectCanvasAppBundlesCacheUsage,
} from '@packages/systems/designer/canvas';
import {DesignerWorkflow} from '@packages/systems/designer/workflows';

/**
 * Hook that handles reporting of Canvas App latency measurements including cache detection
 */
export const useReportCanvasLatency = (
  isLoading: boolean,
  workflow: DesignerWorkflow | undefined
): void => {
  /** Measures and reports initial Canvas App startup latency for each workflow
   * - Measurement starts: When iframe loads in CanvasSeparateOriginContainer
   * - Measurement ends: When initial workflow is fully loaded and rendered
   * - Includes cache detection
   */
  React.useEffect(() => {
    const isInitialLoadComplete = !isLoading;
    const latencyEvent = workflow
      ? CANVAS_APP_STARTUP_LATENCY_MAP[workflow]
      : undefined;

    if (isInitialLoadComplete && latencyEvent) {
      // Collect cache information for canvas app bundles
      const additionalContext = detectCanvasAppBundlesCacheUsage(
        window.location.origin
      );
      reportLatencyFinishedToDesigner(latencyEvent, {}, additionalContext);
    }
  }, [isLoading]);

  /** Measures and reports Canvas App switching latency between workflows (e.g., Preview to Comment mode)
   * - Measurement starts: When iframe loads in CanvasSeparateOriginContainer
   * - Measurement ends: When new workflow is fully loaded and rendered
   */
  React.useEffect(() => {
    if (!isLoading && workflow) {
      const latencyEvent = CANVAS_APP_SWITCH_LATENCY_MAP[workflow];
      if (latencyEvent) {
        reportLatencyFinishedToDesigner(latencyEvent);
      }
    }
  }, [isLoading, workflow]);
};
